import { useNavigate } from 'react-router-dom';
import { useDeleteWebinar } from '@src/hooks';
import { SuccessToast } from '@src/components';
import { toast } from 'react-toastify';
import { EMeetingRoutes } from '@src/constants/routes.constants';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { debounce } from '@src/utils';
import { queryKeys } from '@src/utils/queryKeys';

interface Props {
  onClose: () => void;
  meetingId: string;
}

export const useDeleteModal = ({ onClose, meetingId }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: deleteWebinar } = useDeleteWebinar();
  const [loading, setLoading] = useState(false);

  const deleteApiCall = useCallback(() => {
    setLoading(true);
    deleteWebinar(meetingId, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [queryKeys.webinar.listMeeting] });
        onClose();

        navigate(EMeetingRoutes.HOME);

        setTimeout(() => {
          toast(<SuccessToast message="Webinar meeting has been deleted successfully!" />, {
            className: 'custom-toast-wrapper',
            autoClose: 3000,
            closeButton: false,
            hideProgressBar: true,
            icon: false,
          });
        }, 100);
      },
      onError: (error: any) => {
        toast.error('Failed to delete webinar. Please try again.');
        console.error('Delete error:', error);
      },
      onSettled: () => {
        setLoading(false);
      },
    });
  }, [deleteWebinar, meetingId, navigate, onClose, queryClient]);

  const debouncedDelete = useMemo(() => debounce(deleteApiCall, 300), [deleteApiCall]);

  const handleDeleteWebinar = useCallback(() => {
    debouncedDelete.cancel?.();
    debouncedDelete();
  }, [debouncedDelete]);

  return { handleDeleteWebinar, loading };
};
